import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { DEFAULT_DATETIME_FORMAT, LooseParsingOptions, TsDate, UnixTimestamp, extractLooseParsingOptions, format, isInvalid, isValid, parse } from "@cervval.js/common/src/tools/timestamps.tools";
import { getIconFaName } from "../../../components/icon/iconFa.tools";
import { IconFA6 } from "../../../components/icon/tools/fontAwesome6IconList.tools";
import { DatePickerOptions } from "../../../pickers/datePicker/datePicker";
import { AllDateTimePickerSubOptions, CjsDateTimePicker, DateTimePickerOptions } from "../../../pickers/dateTimePicker/dateTimePicker";
import { TimePickerOptions } from "../../../pickers/timePicker/timePicker";
import { EditorWithPicker, EditorWithPickerOptions, defaultEditorWithPickerOptions } from "../editorWithPicker";


/** interface used to proxy the referenced options from date time picker */
export type DateTimePickerProxyOptions = {
    pickerOptions: WebComponentOptions<DateTimePickerOptions>,
    datePickerOptions: WebComponentOptions<DatePickerOptions>,
    timePickerOptions: WebComponentOptions<TimePickerOptions>
}

export interface DateTimeEditorOptions extends EditorWithPickerOptions, LooseParsingOptions {

}

export const defaultDateTimeEditorOptions: DateTimeEditorOptions = {
    ...defaultEditorWithPickerOptions,
    buttonIcon: { name: getIconFaName(IconFA6.CALENDAR_CLOCK), fixedWidth: true }
};

@CustomElement({
    name: "cjs-date-time-editor",
    elementCssClasses: ["cjs-editor"]
})
export class CjsDateTimeEditor extends EditorWithPicker<UnixTimestamp> {

    /** Attributes options of editor */
    @TypedWebComponentOptions(defaultDateTimeEditorOptions)
    public readonly options!: WebComponentOptions<DateTimeEditorOptions>;

    /** Shortcut to dateTime picker options */
    public get pickerOptions(): DateTimePickerProxyOptions {
        return {
            pickerOptions: this._picker.options,
            datePickerOptions: this._picker.datePickerOptions,
            timePickerOptions: this._picker.timePickerOptions
        };
    }

    /**
     * The DateTimePicker used to edit the value of the input
     */
    protected _picker = new CjsDateTimePicker();

    constructor(options?: Partial<EditorWithPickerOptions>, pickerOptions?: Partial<AllDateTimePickerSubOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);

        if (pickerOptions !== undefined) {
            this.pickerOptions.pickerOptions.setWithoutRefresh(pickerOptions?.pickerOptions);
            this.pickerOptions.datePickerOptions.setWithoutRefresh(pickerOptions?.datePickerOptions);
            this.pickerOptions.timePickerOptions.setWithoutRefresh(pickerOptions?.timePickerOptions);

        }
    }

    /**
     * Refresh picker based on value and options (called before showPopup)
     */
    protected _refreshPicker(): CjsDateTimePicker {
        if (this._value != null) {
            this._picker.setValue(this._value);
            this._picker.refresh();
        } else {// Undefined, reset picker // TODO Create and replace with empty picker
            this._picker = new CjsDateTimePicker(this.pickerOptions);
        }
        return this._picker;
    }

    /**
     * Format the current dateTime to a string which can be display for Humans
     */
    public convertValueToInputValue(ts: UnixTimestamp | undefined): string {
        if (!ts || isInvalid(ts)) { return ""; }
        return format(ts, DEFAULT_DATETIME_FORMAT);
    }

    /**
     * Get value from string input
     */
    protected _convertInputValueToValue(inputValue: string | undefined): UnixTimestamp | undefined {
        if (inputValue == null) { return undefined; }
        // TODO - Align formats parsings to a constant

        // Parse value with mode
        const inputDate: TsDate | undefined = parse(inputValue, DEFAULT_DATETIME_FORMAT, extractLooseParsingOptions(this.options));

        if (inputDate != null && isValid(inputDate.uts)) {
            return inputDate.uts;
        } else {
            return undefined;
        }
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): UnixTimestamp | undefined {
        return this._convertInputValueToValue(this.getInput().input.value);
    }
}
